import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import { Alert, App, Button } from 'antd';
import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import {
  PreSalesAvailabilityResult,
  PreSalesSubmitFormResponse,
  Slot,
} from '../../../dto/model';
import { strings } from '../../../lang';
import { SalesCallCalendar } from '../../appointment-booking/sales-call-calendar';
import { shouldShowPreSalesCustomerPortalTip } from './pre-sales-booking-tips';
import { PreSalesBookingLaneBooking } from './pre-sales-booking-lane-booking';
import type { BookingLaneReasonResult } from './pre-sales-booking-lane-reason-modal';
import { PreSalesQuestionnaireResult } from './pre-sales-questionnaire-result';
import { PreSalesZeegBookingHandler, PreSalesQuestionnaireCustomer } from './pre-sales-questionnaire-types';
import { ZeegBookingLinksInfo } from './pre-sales-questionnaire-utils';
import { usePreSalesFieldSalesBooking } from './use-pre-sales-field-sales-booking';
import { usePreSalesVideoSalesBooking } from './use-pre-sales-video-sales-booking';

type PreSalesQuestionnaireResultContainerProps = {
  submissionResult: PreSalesSubmitFormResponse;
  footerActions?: ReactNode;
  skipFieldSales?: boolean;
  /** Unified (type=ad) flow: the customer portal tip is not shown. */
  unifiedFlow?: boolean;
  bookZeegSlot?: PreSalesZeegBookingHandler;
  hasPendingZeegBooking?: () => boolean;
  bookedZeegSlot?: Slot | null;
  fieldSalesAvailability: PreSalesAvailabilityResult | null;
  fieldSalesAvailabilityChecked: boolean;
  zeegBookingLinksInfo: ZeegBookingLinksInfo | null;
  crmId?: string;
  signupId?: string;
  zipCode?: string;
  customer?: PreSalesQuestionnaireCustomer;
  /** Called once when a booking succeeds (field sales or video). Stops recording as Booked. */
  onBooked?: (slot?: Slot, lane?: string, reason?: BookingLaneReasonResult) => void | Promise<void>;
  onBackToForm: () => void;
};

export const PreSalesQuestionnaireResultContainer = ({
  submissionResult,
  footerActions,
  skipFieldSales = false,
  unifiedFlow = false,
  bookZeegSlot,
  hasPendingZeegBooking,
  bookedZeegSlot,
  fieldSalesAvailability,
  fieldSalesAvailabilityChecked,
  zeegBookingLinksInfo,
  crmId,
  signupId,
  zipCode,
  customer,
  onBooked,
  onBackToForm,
}: PreSalesQuestionnaireResultContainerProps) => {
  const { notification } = App.useApp();
  const [fieldSalesChoice, setFieldSalesChoice] = useState<boolean | null>(null);
  const [isVideoRouteLocked, setIsVideoRouteLocked] = useState(false);
  const [zeegCalendarVariant, setZeegCalendarVariant] = useState<'primary' | 'default'>('primary');
  // The unified (ad) flow has no deal id, so PreSales books field-sales slots against the signup id.
  const fieldSales = usePreSalesFieldSalesBooking({
    crmId,
    dealId: unifiedFlow ? signupId : undefined,
    zipCode,
  });
  const zeegBookingLinkInfo = zeegCalendarVariant === 'default'
    ? zeegBookingLinksInfo?.default ?? zeegBookingLinksInfo?.primary ?? null
    : zeegBookingLinksInfo?.primary ?? null;
  const videoSales = usePreSalesVideoSalesBooking({
    crmId,
    customer,
    zeegBookingLinkInfo,
  });

  // Fire onBooked exactly once when any booking route completes (field-sales calendar,
  // agent-only field sales, or video). reportOutcome upstream is idempotent, but we still
  // guard so re-renders don't spam it.
  const hasBooked = Boolean(fieldSales.bookedSlot || fieldSales.hasConfirmedAgentOnly || videoSales.bookedSlot);
  const bookedReportedRef = useRef(false);
  useEffect(() => {
    if (hasBooked && !bookedReportedRef.current) {
      bookedReportedRef.current = true;
      onBooked?.();
    }
  }, [hasBooked, onBooked]);
  const effectiveFieldSalesAvailability = isVideoRouteLocked ? null : fieldSalesAvailability;
  const fieldSalesQuestionVisible = Boolean(
    !skipFieldSales
    && submissionResult.fieldSalesBookingAllowed
    && effectiveFieldSalesAvailability?.available,
  );
  const hasZeegRouting = Boolean(
    zeegBookingLinkInfo?.ownerSlug
    && zeegBookingLinkInfo.eventTypeSlug,
  );
  const hasDefaultZeegCalendar = Boolean(zeegBookingLinksInfo?.default);
  const hasPendingVideoBooking = () => videoSales.hasPendingBooking() || Boolean(hasPendingZeegBooking?.());

  const chooseZeegCalendar = (variant: 'primary' | 'default') => {
    if (variant === zeegCalendarVariant) return;

    if (hasPendingVideoBooking()) {
      notification.error({
        message: strings.preSalesQuestionnaire.bookingConfirmationPending,
      });
      return;
    }

    setZeegCalendarVariant(variant);
  };

  const chooseFieldSales = async () => {
    if (hasPendingVideoBooking()) {
      notification.error({
        message: strings.preSalesQuestionnaire.bookingConfirmationPending,
      });
      return;
    }

    setIsVideoRouteLocked(false);
    setFieldSalesChoice(true);
    fieldSales.clearResult();
    videoSales.clearResult();

    if (
      !effectiveFieldSalesAvailability?.agent
      || effectiveFieldSalesAvailability.bookableAgent
    ) {
      return;
    }

    await fieldSales.confirmAgentOnly();
  };

  const chooseVideoSales = () => {
    if (fieldSales.hasPendingBooking()) {
      notification.error({
        message: strings.preSalesQuestionnaire.bookingConfirmationPending,
      });
      return;
    }

    fieldSales.cancelAgentOnlyConfirmation();
    setFieldSalesChoice(false);
    setIsVideoRouteLocked(true);
    fieldSales.clearResult();
    videoSales.clearResult();
  };

  const videoCalendar = (
    <div className="bp-presales-result-calendar">
      <strong>{strings.preSalesQuestionnaire.videoCalendarTitle}</strong>
      {hasZeegRouting ? (
        <SalesCallCalendar
          key={zeegCalendarVariant}
          opportunityId=""
          signupId=""
          sourceSystemHeader="presales-zeeg"
          skipConfirmationAfterBooking
          loadSlots={videoSales.loadSlots}
          bookSlot={bookZeegSlot && zeegBookingLinkInfo
            ? (slot, token) => bookZeegSlot(slot, token, zeegBookingLinkInfo)
            : videoSales.bookSlot}
          onBooking={videoSales.handleBooking}
        />
      ) : (
        <Alert
          type="error"
          showIcon
          message={strings.preSalesQuestionnaire.videoBookingConfigurationError}
        />
      )}
      {hasDefaultZeegCalendar && (
        <div
          className="bp-presales-result-calendar__switch"
          role="group"
          aria-label={strings.preSalesQuestionnaire.videoCalendarSwitchLabel}
        >
          <p>{strings.preSalesQuestionnaire.videoCalendarSwitchDescription}</p>
          <div className="bp-presales-result-calendar__switch-controls">
            <Button
              type="default"
              shape="circle"
              icon={<LeftOutlined />}
              aria-label={strings.preSalesQuestionnaire.videoPrimaryCalendarLabel}
              disabled={zeegCalendarVariant === 'primary'}
              onClick={() => chooseZeegCalendar('primary')}
            />
            <span aria-live="polite">
              {zeegCalendarVariant === 'primary'
                ? strings.preSalesQuestionnaire.videoPrimaryCalendarPosition
                : strings.preSalesQuestionnaire.videoDefaultCalendarPosition}
            </span>
            <Button
              type="default"
              shape="circle"
              icon={<RightOutlined />}
              aria-label={strings.preSalesQuestionnaire.videoDefaultCalendarLabel}
              disabled={zeegCalendarVariant === 'default'}
              onClick={() => chooseZeegCalendar('default')}
            />
          </div>
        </div>
      )}
    </div>
  );

  const fieldSalesCalendar = (
    <div className="bp-presales-result-calendar">
      <strong>{strings.preSalesQuestionnaire.fieldSalesCalendarTitle}</strong>
      <SalesCallCalendar
        opportunityId=""
        signupId=""
        sourceSystemHeader="presales-field-sales"
        skipConfirmationAfterBooking
        loadSlots={fieldSales.loadSlots}
        bookSlot={fieldSales.bookSlot}
        onBooking={fieldSales.handleBooking}
      />
    </div>
  );

  // Unified (type=ad) flow: booking is driven by the eligible lanes returned on submit. The first lane
  // in the priority list is selected by default and drives the initial calendar request.
  const lanePriority = submissionResult.lanePriority ?? [];
  if (unifiedFlow && lanePriority.length > 0) {
    return (
      <PreSalesQuestionnaireResult
        submissionResult={submissionResult}
        footerActions={footerActions}
        bookedFieldSalesSlot={null}
        hasConfirmedAgentOnlyFieldSales={false}
        bookedVideoSlot={bookedZeegSlot ?? null}
        fieldSalesAvailabilityChecked
        fieldSalesQuestionVisible={false}
        fieldSalesChoice={null}
        fieldSalesAvailability={null}
        isConfirmingAgentOnlyFieldSales={false}
        fieldSalesCalendar={null}
        videoCalendar={(
          <PreSalesBookingLaneBooking
            signupId={signupId}
            crmId={crmId}
            zipCode={zipCode}
            customer={customer}
            lanes={lanePriority}
            bookZeegSlot={bookZeegSlot}
            onBooked={onBooked}
          />
        )}
        showCustomerPortalTip={false}
        onChooseFieldSales={() => undefined}
        onChooseVideoSales={() => undefined}
        onBackToForm={onBackToForm}
      />
    );
  }

  return (
    <PreSalesQuestionnaireResult
      submissionResult={submissionResult}
      footerActions={footerActions}
      bookedFieldSalesSlot={fieldSales.bookedSlot}
      hasConfirmedAgentOnlyFieldSales={fieldSales.hasConfirmedAgentOnly}
      bookedVideoSlot={bookedZeegSlot ?? videoSales.bookedSlot}
      fieldSalesAvailabilityChecked={skipFieldSales || fieldSalesAvailabilityChecked}
      fieldSalesQuestionVisible={fieldSalesQuestionVisible}
      fieldSalesChoice={fieldSalesChoice}
      fieldSalesAvailability={effectiveFieldSalesAvailability}
      isConfirmingAgentOnlyFieldSales={fieldSales.isConfirmingAgentOnly}
      fieldSalesCalendar={fieldSalesCalendar}
      videoCalendar={videoCalendar}
      showCustomerPortalTip={!unifiedFlow && shouldShowPreSalesCustomerPortalTip(customer?.customerSource)}
      onChooseFieldSales={chooseFieldSales}
      onChooseVideoSales={chooseVideoSales}
      onBackToForm={() => {
        if (hasPendingZeegBooking?.()) {
          notification.error({ message: strings.preSalesQuestionnaire.bookingConfirmationPending });
          return;
        }
        onBackToForm();
      }}
    />
  );
};
